const ONBOARDING_STEP_KEY = "onboardingStep";
const ONBOARDING_COMPLETE_KEY = "onboardingComplete";

export const setOnboardingStep = (step) => {
  localStorage.setItem(ONBOARDING_STEP_KEY, String(step));
};

export const getOnboardingStep = () => {
  const step = parseInt(localStorage.getItem(ONBOARDING_STEP_KEY), 10);
  return Number.isNaN(step) ? 2 : step;
};

export const setOnboardingComplete = () => {
  localStorage.setItem(ONBOARDING_COMPLETE_KEY, "true");
};

export const isOnboardingComplete = () => {
  return localStorage.getItem(ONBOARDING_COMPLETE_KEY) === "true";
};

export const clearOnboardingState = () => {
  localStorage.removeItem(ONBOARDING_STEP_KEY);
  localStorage.removeItem(ONBOARDING_COMPLETE_KEY);
};

/**
 * getAuthToken
 *
 * Retrieves the active authorization token.
 * 1. PRIMARY: Checks current tab's sessionStorage (auth_token).
 *    Do NOT use route-based token selection when a valid sessionStorage token exists.
 * 2. FALLBACK: If sessionStorage is empty (e.g. freshly opened tab),
 *    seeds from role-specific localStorage credentials based on path or availability.
 */
export const getAuthToken = () => {
  // 1. PRIMARY: Tab-specific sessionStorage
  const sessionToken = sessionStorage.getItem("auth_token");
  if (sessionToken) {
    return sessionToken;
  }

  // 2. FALLBACK: Only when tab has no session (e.g. direct link in new tab)
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  if (
    pathname.startsWith("/worklist") ||
    pathname.startsWith("/settings") ||
    pathname.startsWith("/subscription") ||
    pathname.startsWith("/marketing")
  ) {
    const saToken = localStorage.getItem("superadmin_token");
    if (saToken) {
      sessionStorage.setItem("auth_token", saToken);
      sessionStorage.setItem("auth_role", localStorage.getItem("superadmin_role") || "SuperAdmin");
      sessionStorage.setItem("auth_userId", localStorage.getItem("superadmin_userId") || "");
      return saToken;
    }
  }

  if (
    pathname.startsWith("/onboarding") ||
    pathname.startsWith("/payment") ||
    pathname.startsWith("/my-plan")
  ) {
    const clientToken = localStorage.getItem("client_token");
    if (clientToken) {
      sessionStorage.setItem("auth_token", clientToken);
      sessionStorage.setItem("auth_role", localStorage.getItem("client_role") || "Client");
      sessionStorage.setItem("auth_userId", localStorage.getItem("client_userId") || "");
      return clientToken;
    }
  }

  // 3. Generic fallback
  return localStorage.getItem("token") || "";
};

/**
 * getAuthRole
 *
 * Retrieves the active role for the current tab.
 * 1. PRIMARY: Checks sessionStorage (auth_role).
 * 2. FALLBACK: If empty, checks path or localStorage.
 */
export const getAuthRole = () => {
  const sessionRole = sessionStorage.getItem("auth_role");
  if (sessionRole) {
    return sessionRole;
  }

  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  if (
    pathname.startsWith("/worklist") ||
    pathname.startsWith("/settings") ||
    pathname.startsWith("/subscription") ||
    pathname.startsWith("/marketing")
  ) {
    if (localStorage.getItem("superadmin_token")) {
      const saRole = localStorage.getItem("superadmin_role") || "SuperAdmin";
      sessionStorage.setItem("auth_role", saRole);
      return saRole;
    }
  }

  if (
    pathname.startsWith("/onboarding") ||
    pathname.startsWith("/payment") ||
    pathname.startsWith("/my-plan")
  ) {
    if (localStorage.getItem("client_token")) {
      const clientRole = localStorage.getItem("client_role") || "Client";
      sessionStorage.setItem("auth_role", clientRole);
      return clientRole;
    }
  }

  return localStorage.getItem("role") || "";
};

/**
 * getAuthUserId
 *
 * Retrieves the active user ID for the current tab.
 */
export const getAuthUserId = () => {
  const sessionUserId = sessionStorage.getItem("auth_userId");
  if (sessionUserId) {
    return sessionUserId;
  }

  const role = getAuthRole();
  if (role === "SuperAdmin") {
    return localStorage.getItem("superadmin_userId") || "";
  }
  if (role === "Client" || role === "Admin") {
    return localStorage.getItem("client_userId") || "";
  }
  return localStorage.getItem("userId") || "";
};

/**
 * loginWithToken
 *
 * Stores credentials in current tab's sessionStorage (primary)
 * and in role-specific localStorage keys.
 * SuperAdmin login never overwrites Client credentials, and vice versa.
 */
export const loginWithToken = (data, navigate) => {
  const { token, user, next_step } = data;
  const role = user?.role;
  const userId = user?.id;

  // 1. Set current tab's sessionStorage (PRIMARY active session)
  sessionStorage.setItem("auth_token", token);
  sessionStorage.setItem("auth_role", role ?? "");
  sessionStorage.setItem("auth_userId", String(userId ?? ""));

  // 2. Persist to role-specific keys in localStorage
  if (role === "SuperAdmin") {
    localStorage.setItem("superadmin_token", token);
    localStorage.setItem("superadmin_role", role);
    localStorage.setItem("superadmin_userId", String(userId ?? ""));
  } else if (role === "Client" || role === "Admin") {
    localStorage.setItem("client_token", token);
    localStorage.setItem("client_role", role);
    localStorage.setItem("client_userId", String(userId ?? ""));
  }

  // 3. Keep generic keys updated for backwards compatibility
  localStorage.setItem("token", token);
  localStorage.setItem("role", role ?? "");
  localStorage.setItem("userId", String(userId ?? ""));

  const parsedNextStep = next_step != null ? parseInt(next_step, 10) : null;

  if (role === "Admin") {
    navigate("/onboarding");
  } else if (role === "SuperAdmin") {
    navigate("/worklist/clientonboarding");
  } else if (role === "Client") {
    if (parsedNextStep != null && !Number.isNaN(parsedNextStep)) {
      setOnboardingStep(parsedNextStep);
    } else {
      setOnboardingStep(2);
    }
    navigate("/onboarding");
  } else {
    navigate("/");
  }
};

/**
 * logout
 *
 * Clears only the current tab's session and that specific role's localStorage credentials.
 * The other role's credentials in localStorage and other tabs remain untouched.
 */
export const logout = (navigate) => {
  const role = getAuthRole();

  // Clear current tab's sessionStorage
  sessionStorage.removeItem("auth_token");
  sessionStorage.removeItem("auth_role");
  sessionStorage.removeItem("auth_userId");

  // Clear only that role's localStorage credentials
  if (role === "SuperAdmin") {
    localStorage.removeItem("superadmin_token");
    localStorage.removeItem("superadmin_role");
    localStorage.removeItem("superadmin_userId");
  } else if (role === "Client" || role === "Admin") {
    localStorage.removeItem("client_token");
    localStorage.removeItem("client_role");
    localStorage.removeItem("client_userId");
    clearOnboardingState();
  }

  // Clean generic keys if they belonged to this role
  if (localStorage.getItem("role") === role) {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("userId");
  }

  localStorage.removeItem("activeNav");
  localStorage.removeItem("activeChildPath");
  localStorage.removeItem("carepreneur_id");

  if (navigate) {
    navigate("/login");
  } else {
    window.location.href = "/login";
  }
};

